import is from '@sindresorhus/is';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const CURSOR_STYLE = { height: '1px' } as const;

type UseInfiniteScrollCursorParams = {
    readonly hasMore: boolean;
    readonly isLoading: boolean;
    readonly onIntersect: () => void;
    readonly rootMargin?: string;
};

export function useInfiniteScrollCursor({
    hasMore,
    isLoading,
    onIntersect,
    rootMargin = '200px',
}: UseInfiniteScrollCursorParams): ReactElement | null {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const cursorElementRef = useRef<HTMLElement | null>(null);

    useEffect(() => {
        if (!is.nullOrUndefined(observerRef.current)) {
            observerRef.current.disconnect();
        }

        if (isLoading || !hasMore) {
            return;
        }

        observerRef.current = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                if (entry?.isIntersecting === true) {
                    onIntersect();
                }
            },
            {
                rootMargin,
                threshold: 0.01,
            },
        );

        if (!is.nullOrUndefined(cursorElementRef.current)) {
            observerRef.current.observe(cursorElementRef.current);
        }

        return () => {
            if (!is.nullOrUndefined(observerRef.current)) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, [isLoading, hasMore, onIntersect, rootMargin]);

    const cursorRef = useCallback((element: HTMLElement | null) => {
        cursorElementRef.current = element;

        if (!is.nullOrUndefined(observerRef.current) && !is.nullOrUndefined(element)) {
            observerRef.current.observe(element);
        }
    }, []);

    return useMemo(() => {
        if (!hasMore) {
            return null;
        }

        return <div ref={cursorRef} style={CURSOR_STYLE} />;
    }, [hasMore, cursorRef]);
}
